import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { CANONICAL_TARGETS, HREFLANG_ROLES, SITE_STATUSES } from '@seo/contracts';
import { useTRPC } from '../lib/trpc';
import { RowEditor } from '../components/RowEditor';

/**
 * Тех-шар сайта, редагований поштучно.
 *
 * Реєстр сайтів був write-once, а `canonicalTarget`, `hreflangRole` і пара писались **лише**
 * масовими операціями — тобто виправити один домен означало запустити масову операцію на один
 * домен, із сухим прогоном, діффом і гейтом двох пар очей. Мокап тримає ці поля у вкладці
 * «Тех» картки сайту (рядок 3261), і це вона.
 *
 * `domain` не редагується: перейменування домена — це інший сайт. Він публікується на іншому
 * хості, має інший канонікал, інші зворотні посилання й інший GSC-property.
 */
export interface EditableSite {
  id: string;
  domain: string;
  status: string;
  canonicalTarget: string;
  canonicalSiteId: string | null;
  hreflangRole: string;
  pairedSiteId: string | null;
  templateId: string | null;
  /** Необовʼязкові: `SitesPage`/`RegistryEditors` досі не передають їх, і форма це терпить. */
  hostingPoolId?: string | null;
  hostingPoolName: string | null;
  registrarAccountId?: string | null;
}

export function SiteEditor({
  row,
  onClose,
}: {
  row: EditableSite;
  onClose: () => void;
}): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  /** Кандидати в пару й у канонічний сайт. Той самий реєстр, звужений скоупом на сервері. */
  const candidates = useQuery(
    trpc.sites.list.queryOptions({ pagination: { limit: 200, direction: 'desc' } }),
  );
  const pools = useQuery(trpc.sites.hostingPools.queryOptions());
  /** Досі без жодного викликача: реєстратор можна було вписати лише руками в базу. */
  const registrars = useQuery(trpc.infra.registrars.queryOptions());

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.sites.pathKey() });
  };

  const update = useMutation(trpc.sites.update.mutationOptions({ onSuccess: refresh }));
  const remove = useMutation(
    trpc.sites.delete.mutationOptions({
      onSuccess: async () => {
        await refresh();
        onClose();
      },
    }),
  );

  const form = useForm({
    initialValues: {
      status: row.status,
      canonicalTarget: row.canonicalTarget,
      canonicalSiteId: row.canonicalSiteId,
      hreflangRole: row.hreflangRole,
      pairedSiteId: row.pairedSiteId,
      hostingPoolId: row.hostingPoolId ?? null,
      registrarAccountId: row.registrarAccountId ?? null,
    },
  });

  const siteOptions = (candidates.data?.items ?? [])
    .filter((site) => site.id !== row.id)
    .map((site) => ({ value: site.id, label: site.domain }));

  return (
    <RowEditor
      title={row.domain}
      testId="site"
      zone="site_seo"
      onClose={onClose}
      busy={update.isPending}
      error={update.error}
      deleting={remove.isPending}
      deleteError={remove.error}
      confirmingDelete={confirming}
      onConfirmingDeleteChange={setConfirming}
      deletePrompt={`Видалити «${row.domain}» з реєстру? Разом із ним зникнуть його сторінки, лінки та інциденти.`}
      onDelete={() => {
        remove.mutate({ id: row.id });
      }}
      onSubmit={form.onSubmit((values) => {
        update.mutate({
          id: row.id,
          patch: {
            status: values.status as (typeof SITE_STATUSES)[number],
            canonicalTarget: values.canonicalTarget as (typeof CANONICAL_TARGETS)[number],
            canonicalSiteId: values.canonicalSiteId,
            hreflangRole: values.hreflangRole as (typeof HREFLANG_ROLES)[number],
            pairedSiteId: values.pairedSiteId,
            /*
             * `row.hostingPoolId`/`row.registrarAccountId` необовʼязкові: старіші виклики
             * (`SitesPage`, `RegistryEditors`) їх не передають, тож початкове значення тут —
             * завжди `null`, а не «реального пула нема». Надсилаємо поле лише коли оператор
             * його справді торкнувся — інакше форма без реальних даних мовчки обнулила б
             * призначений пул чи реєстратора при кожному звичайному «Зберегти».
             */
            ...(form.isDirty('hostingPoolId') ? { hostingPoolId: values.hostingPoolId } : {}),
            ...(form.isDirty('registrarAccountId')
              ? { registrarAccountId: values.registrarAccountId }
              : {}),
          },
        });
      })}
    >
      <Select
        size="xs"
        label="Статус"
        allowDeselect={false}
        data={[...SITE_STATUSES]}
        data-testid="edit-site-status"
        style={{ width: 110 }}
        {...form.getInputProps('status')}
      />
      <Select
        size="xs"
        label="Канонікал"
        allowDeselect={false}
        data={[...CANONICAL_TARGETS]}
        data-testid="edit-site-canonical-target"
        style={{ width: 110 }}
        {...form.getInputProps('canonicalTarget')}
      />
      {/*
       * Поле зʼявляється лише для `site`: для `self` воно не має сенсу, а для `drop` ціль
       * бере з пари. Показувати його завжди означало б пропонувати оператору вибір, який
       * сервер потім проігнорує.
       */}
      {form.values.canonicalTarget === 'site' ? (
        <Select
          size="xs"
          label="Канонічний сайт"
          searchable
          clearable
          data={siteOptions}
          data-testid="edit-site-canonical-site"
          style={{ width: 200 }}
          {...form.getInputProps('canonicalSiteId')}
        />
      ) : null}
      <Select
        size="xs"
        label="Роль hreflang"
        allowDeselect={false}
        data={[...HREFLANG_ROLES]}
        data-testid="edit-site-hreflang-role"
        style={{ width: 120 }}
        {...form.getInputProps('hreflangRole')}
      />
      <Select
        size="xs"
        label="Пара (дроп)"
        searchable
        clearable
        data={siteOptions}
        data-testid="edit-site-paired"
        style={{ width: 200 }}
        {...form.getInputProps('pairedSiteId')}
      />
      <Select
        size="xs"
        label="Хостинг-пул"
        placeholder="без пулу"
        clearable
        searchable
        data={(pools.data ?? []).map((pool) => ({ value: pool.id, label: pool.name }))}
        data-testid="edit-site-hosting-pool"
        style={{ width: 170 }}
        {...form.getInputProps('hostingPoolId')}
      />
      {/*
       * `infra.registrars` не мав жодного викликача: реєстратора можна було вписати сайту
       * лише руками в базу, тож картка домена не могла сказати, хто його продовжує.
       */}
      <Select
        size="xs"
        label="Реєстратор"
        placeholder="не вказано"
        clearable
        searchable
        data={(registrars.data ?? []).map((r) => ({ value: r.id, label: `${r.name} (${r.provider})` }))}
        data-testid="edit-site-registrar"
        style={{ width: 200 }}
        {...form.getInputProps('registrarAccountId')}
      />
    </RowEditor>
  );
}
